import React, { useState, useEffect, useCallback } from "react";
import privateAxios from "../../utils/axiosPrivate";
import PropTypes from "prop-types";
import { FaCheck } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import { showToast, isValidPhoneLength } from "../../utils/utils";
import "react-phone-input-2/lib/style.css";
import Modalstyle from "./modal.module.scss";
import "../../assets/scss/common.scss";
import {
  AU_PHONE_REGEX,
  NZ_PHONE_REGEX,
  showToastType,
  WHITESPACE_REGEX
} from "../../utils/constants/appConstants";
import {
  buttonName,
  commonErrMsg,
  identityText,
  signup
} from "../../locales/en";
import {
  getFetchCountryCodeURL,
  getOnboardSendLinkTokenUrl
} from "../../utils/constants/apiEndPoints";
import CommonModal from "./CommonModal";
import ErrorBoundary from "../error-boundary";
import ModalFallback from "../error-boundary/fallback/ModalFallback";

const OnboardingIdentityTokenModal = ({
  isOpen,
  toggle,
  getToken,
  successMsg
}) => {
  const [selectCountry, setSelectCountry] = useState("au");
  const [country, setCountry] = useState("au");
  const [validatePhnumber, setValidatePhnumber] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const initialValues = {
    phoneNumber: "",
    error: ""
  };
  const [values, setValues] = useState(initialValues);
  const initialErrors = {
    phoneNumber: ""
  };
  const [errors, setErrors] = useState(initialErrors);

  const fetchUserLocation = useCallback(async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      const data = await response.json();
      if (data.country_code === "NZ") {
        setCountry("nz");
      } else {
        setCountry("au");
      }
    } catch {
      // handle error catch
    }
  }, []);

  const fetchCountryCode = useCallback(async (phoneNumber, country) => {
    try {
      const response = await privateAxios.get(getFetchCountryCodeURL, {
        params: { number: phoneNumber, countryCode: country }
      });
      if (response.status === 200) {
        setValidatePhnumber(true);
      } else {
        setValidatePhnumber(false);
      }
    } catch {
      setValidatePhnumber(false);
    }
  }, []);

  const selectedCountry = (phoneNumber) => {
    const countryCode = phoneNumber.slice(0, 2);
    let country = null;
    if (countryCode === "61") {
      country = "au";
    } else if (countryCode === "64") {
      country = "nz";
    }
    return country;
  };

  const requestPhoneNumber = (phoneNumnber) => {
    const result = phoneNumnber.slice(2).replace(/[()-\s]/g, "");
    return result;
  };

  const handlePhInputChange = (field, value, countryCode) => {
    setValues({ ...values, [field]: value });
    setValidatePhnumber(false);

    if (selectCountry !== countryCode) {
      const result = validateField(field, value);
      if (result !== "") return;
      const phoneNumber = requestPhoneNumber(value);
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };

  const handleBlur = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value
    }));
    // Trigger validation for the specific field
    const result = validateField(field, value);
    if (result !== "") return;
    const phoneNumber = requestPhoneNumber(values.phoneNumber);
    if (phoneNumber && phoneNumber !== value) {
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "phoneNumber": {
        const trimmedValue = value.replace(WHITESPACE_REGEX, "");
        const numericValue = value.trim().replace(/[^\d]/g, "");

        if (
          AU_PHONE_REGEX.test(trimmedValue) ||
          NZ_PHONE_REGEX.test(trimmedValue)
        ) {
          error = signup?.errors.startnumber;
        } else if (
          trimmedValue === "61" ||
          trimmedValue === "64" ||
          trimmedValue === ""
        ) {
          error = signup?.errors?.numberRequired;
        } else if (!isValidPhoneLength(numericValue)) {
          error = signup?.errors?.numberRequired;
        }
        break;
      }

      default:
        break;
    }

    if (typeof error === "object" && !React.isValidElement(error)) {
      error = Object.values(error).flat().join(", ");
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error
    }));

    return error;
  };

  const handleCloseModal = () => {
    toggle();
    setErrors({
      phoneNumber: ""
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phoneNumberError = validateField(
      signup.signupBox.phoneNumber,
      values.phoneNumber
    );

    const errors = {
      phoneNumber: phoneNumberError
    };

    setErrors(errors);
    // Check if any errors exist
    const hasErrors = Object.values(errors).some((error) => error.length > 0);
    if (hasErrors) {
      return;
    }

    // Convert the phone number to E.164 format
    const e164PhoneNumber = `+${values.phoneNumber.replace(/\D/g, "")}`;

    try {
      const payloadData = {
        phoneNumber: e164PhoneNumber,
        token: getToken
      };
      const response = await privateAxios.post(
        getOnboardSendLinkTokenUrl,
        {},
        {
          params: payloadData
        }
      );
      if (response.status === 200) {
        setShowSpinner(true);
        showToast(response.data.message, showToastType.success);
        document.body.classList.add("scroll_hide");
      }
      toggle();
      setValues({
        phoneNumber: ""
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.join(", ") ||
        error.response?.data?.message ||
        commonErrMsg?.item4;
      showToast(errorMessage, showToastType.error);
    }
  };

  useEffect(() => {
    fetchUserLocation();
  }, [fetchUserLocation]);

  useEffect(() => {
    if (successMsg) {
      const makeDisable = successMsg !== true;
      setShowSpinner(makeDisable);
    }
  }, [successMsg]);

  if (showSpinner) {
    return (
      <div className="spinner-wrap bg-light loaderposition">
        <div className="snipper-wrap">
          <div className="spinner-border custom-spinner-color" />
          <h3>{identityText.onboardingIntgModal.thismayTake}</h3>
        </div>
      </div>
    );
  }

  const footerButtons = [
    {
      label: buttonName?.close,
      className: "btn btn-secondary",
      onClick: handleCloseModal
    },
    {
      label: buttonName?.sendLink,
      className: "btn btn-primary",
      onClick: handleSubmit
    }
  ];

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title={identityText?.sendLink}
      footerButtons={footerButtons}
    >
      <ErrorBoundary fallback={<ModalFallback />}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <div className={`${Modalstyle.modalInputwrap}`}>
              <p>{identityText.onboardingIntgModal.enterYourPh}</p>
            </div>
          </div>
          <div className="col-md-12">
            <div
              className={`${Modalstyle.modalInputwrap} ${Modalstyle.validatePhoneNumber}`}
            >
              <PhoneInput
                inputProps={{
                  name: "phone_number",
                  id: "phone_number"
                }}
                className="ph-with-country-code"
                country={country}
                value={values.phoneNumber}
                countryCodeEditable={false}
                onlyCountries={["nz", "au"]}
                areaCodes={{ au: ["61"], nz: ["64"] }}
                masks={{ au: "(..) .... ...", nz: "(..) .... ...." }}
                onChange={(value, event) => {
                  setSelectCountry(event.countryCode);
                  handlePhInputChange("phoneNumber", value, event.countryCode);
                }}
                onBlur={() => handleBlur("phoneNumber", values.phoneNumber)}
              />
              {validatePhnumber && (
                <div className="small primary-color">
                  <FaCheck /> {signup?.signupBox.phoneOkay}
                </div>
              )}
              {errors.phoneNumber && (
                <div className="text-danger small">{errors.phoneNumber}</div>
              )}
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </CommonModal>
  );
};

OnboardingIdentityTokenModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  getToken: PropTypes.string.isRequired,
  toggle: PropTypes.func.isRequired,
  successMsg: PropTypes.bool
};
export default OnboardingIdentityTokenModal;
